<template>
  <div
    class="base-field"
    :class="{
      'has-error': showError,
      'has-description': description || hasDescriptionSlot
    }"
  >
    <div class="base-field__header">
      <label class="base-field__label" :for="labelFor">
        {{ label }}
        <span v-if="optional" class="base-field__optional">{{
          $t('utils:common.optional')
        }}</span>
      </label>

      <slot name="append"></slot>
    </div>
    <p v-if="description" class="base-field__description">
      {{ description }}
    </p>
    <div v-if="hasDescriptionSlot" class="base-field__description">
      <slot name="description" />
    </div>
    <slot />
    <p v-if="error" class="base-field__error" data-cy="form-field-error">
      {{ error }}
    </p>
    <div v-if="hasError" class="base-field__error">
      <slot name="error" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaseFormField',
  props: {
    label: {
      type: String,
      required: true
    },
    description: {
      type: String,
      default: ''
    },
    labelFor: {
      type: String,
      default: null
    },
    error: {
      type: String,
      default: ''
    },
    hasError: {
      type: Boolean,
      default: false
    },
    optional: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    showError() {
      return !!this.error || this.hasError;
    },
    hasDescriptionSlot() {
      return !!this.$slots.description;
    }
  }
};
</script>

<style lang="scss" scoped>
.base-field {
  margin-bottom: spacing('8x');

  &__header {
    display: flex;
    justify-content: space-between;
    margin-bottom: spacing('2x');
  }

  &.has-description {
    .base-field__header {
      margin-bottom: spacing('0-5x');
    }
  }

  &__label {
    @include font-size(md);
    display: inline-block;
    text-align: left;
    font-weight: bold;
    color: color('form-default-label-text');
  }

  &__description,
  &__description * {
    @include font-size(md);
    margin-top: 0;
    margin-bottom: spacing('2x');
    color: color(secondary-text);
  }

  &__error {
    color: color(form-error-message-text);
    margin-top: spacing('2x');
    @include font-size(md);
  }

  textarea {
    display: block;
    width: 100%;
    margin-bottom: spacing('4x');
    padding: spacing('2x') spacing('4x');
    @include font-size(md);
    border: 1px solid color(form-default-input-lines);
    border-radius: rounding('1x');
    min-height: rem-calc(120px);

    @include generate-form-selecting-states;
  }

  &.has-error {
    .base-field__label {
      color: initial;
    }

    input,
    select,
    textarea {
      border: 1px color(form-error-input-lines) solid;

      &:hover {
        box-shadow: get-form-selecting-box-shadow(error);
      }

      &:focus-visible {
        @include generate-form-elements-focus();
      }
    }
  }

  .base-field__optional {
    @include font-size(md);
    color: color(secondary-text);
    margin-left: spacing('2x');
    font-weight: normal;

    [dir='rtl'] & {
      margin-left: 0;
      margin-right: spacing('2x');
    }
  }
}
</style>
